import { useRef, useState } from "react";
import { Map as MapIcon, Star, UserRound } from "lucide-react";

const SNAPS = { peek: 0.2, half: 0.52, full: 1 };
const ORDER = ["peek", "half", "full"];
const TOP_GAP = 88;

export function BottomSheet({ snap, setSnap, header, children, testid = "drv-bottom-sheet", bottom = 0 }) {
  const [drag, setDrag] = useState(null);
  const start = useRef(null);
  const vh = window.innerHeight;
  const fullH = vh - TOP_GAP - (typeof bottom === "number" ? bottom : 56);
  const heightOf = (s) => (s === "full" ? fullH : vh * SNAPS[s]);
  const base = heightOf(snap);
  const h = drag != null ? Math.min(fullH, Math.max(96, base - drag)) : base;
  const down = (e) => {
    if (e.target.closest("button, a, input, select, textarea")) return;
    start.current = { y: e.clientY, base };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag(0);
  };
  const move = (e) => { if (start.current) setDrag(e.clientY - start.current.y); };
  const up = () => {
    if (!start.current) return;
    const moved = drag || 0, b = start.current.base;
    start.current = null;
    setDrag(null);
    const i = ORDER.indexOf(snap);
    if (Math.abs(moved) < 6) { setSnap(ORDER[(i + 1) % ORDER.length]); return; }
    if (Math.abs(moved) > 40) { setSnap(ORDER[Math.max(0, Math.min(ORDER.length - 1, i + (moved < 0 ? 1 : -1)))]); return; }
    const target = b - moved;
    setSnap(ORDER.reduce((a, s) => (Math.abs(heightOf(s) - target) < Math.abs(heightOf(a) - target) ? s : a), snap));
  };
  return (
    <section data-testid={testid} data-snap={snap} style={{ height: h, bottom, transition: drag != null ? "none" : "height 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)" }}
      className="absolute inset-x-0 z-[700] flex flex-col rounded-t-[22px] bg-white shadow-[0_-10px_30px_rgba(10,35,74,0.18)]">
      <div onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} className="relative flex-shrink-0 cursor-grab touch-none select-none pt-2" data-testid="drv-sheet-handle">
        <div className="mx-auto mb-1.5 h-1.5 w-10 rounded-full bg-slate-300" />
        {header}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
    </section>
  );
}

const TABS = [["explore", "Explore", MapIcon], ["saved", "Saved", Star], ["profile", "Profile", UserRound]];

export function TabBar({ tab, setTab, savedCount, desktop }) {
  if (desktop) {
    return (
      <div className="grid grid-cols-3 gap-1 border-b border-slate-200 bg-slate-50 p-1.5" data-testid="drv-tabbar">
        {TABS.map(([k, label, I]) => (
          <button key={k} data-testid={`drv-tab-${k}`} onClick={() => setTab(k)} className={`inline-flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-white text-[#0A234A] shadow-sm" : "text-slate-500 hover:text-[#0A234A]"}`}>
            <I className="h-3.5 w-3.5" />{label}{k === "saved" && savedCount > 0 && <span className="font-mono text-[10px] text-slate-400">{savedCount}</span>}
          </button>
        ))}
      </div>
    );
  }
  return (
    <nav className="absolute inset-x-0 bottom-0 z-[800] grid grid-cols-3 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)]" data-testid="drv-tabbar">
      {TABS.map(([k, label, I]) => (
        <button key={k} data-testid={`drv-tab-${k}`} onClick={() => setTab(k)} className={`flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors ${tab === k ? "text-[#FF5722]" : "text-slate-500"}`}>
          <span className={`flex h-7 w-14 items-center justify-center rounded-full transition-colors ${tab === k ? "bg-orange-100" : ""}`}><I className={`h-5 w-5 ${tab === k && k === "saved" ? "fill-[#FF5722]" : ""}`} /></span>
          {label}
        </button>
      ))}
    </nav>
  );
}
